/**
 * ProfitCalculator - Calculadora de Rentabilidade DINÂMICA
 * 
 * VERSÃO 3.0 - Controles inteligentes com ajuste ponto a ponto
 * Fórmula: Preço Produtor = Referência × (DRC/100) × (Taxa Participação/100)
 * 
 * Baseado em estudos da CNA/FAESP e IEA/SAA-SP
 * Fontes: IEA/CNA, APABOR, CONAB
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useTranslation } from '../../translations';

// Componente de controle numérico com setas
const NumberStepper = ({ 
  value, 
  onChange, 
  min, 
  max, 
  step = 1, 
  unit = '%',
  label,
  helpText,
  presets = [],
  colorClass = 'green'
}) => {
  const numValue = parseFloat(value) || min;
  
  const increment = () => {
    const newValue = Math.min(numValue + step, max);
    onChange(newValue.toString());
  };
  
  const decrement = () => {
    const newValue = Math.max(numValue - step, min);
    onChange(newValue.toString());
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    if (val === '' || val === '-') {
      onChange(val);
      return;
    }
    const num = parseFloat(val);
    if (!isNaN(num)) {
      onChange(Math.min(Math.max(num, min), max).toString());
    }
  };

  const colorClasses = {
    green: {
      button: 'bg-green-600 hover:bg-green-700 text-white',
      input: 'focus:ring-green-500 focus:border-green-500',
      preset: 'bg-green-600 text-white',
      presetInactive: 'bg-gray-100 hover:bg-gray-200 text-gray-700'
    },
    purple: {
      button: 'bg-purple-600 hover:bg-purple-700 text-white',
      input: 'focus:ring-purple-500 focus:border-purple-500',
      preset: 'bg-purple-600 text-white',
      presetInactive: 'bg-gray-100 hover:bg-gray-200 text-gray-700'
    },
    orange: {
      button: 'bg-orange-600 hover:bg-orange-700 text-white',
      input: 'focus:ring-orange-500 focus:border-orange-500',
      preset: 'bg-orange-600 text-white',
      presetInactive: 'bg-gray-100 hover:bg-gray-200 text-gray-700'
    }
  };

  const colors = colorClasses[colorClass] || colorClasses.green;

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      
      <div className="flex items-center gap-2">
        {/* Botão decrementar */}
        <button
          onClick={decrement}
          disabled={numValue <= min}
          className={`w-10 h-10 rounded-lg font-bold text-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${colors.button}`}
        >
          −
        </button>
        
        {/* Input central */}
        <div className="relative flex-1">
          <input
            type="number"
            value={value}
            onChange={handleInputChange}
            min={min}
            max={max}
            step={step}
            className={`w-full p-3 pr-10 text-center text-lg font-bold border border-gray-300 rounded-lg focus:ring-2 ${colors.input}`}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
            {unit}
          </span>
        </div>
        
        {/* Botão incrementar */}
        <button
          onClick={increment}
          disabled={numValue >= max}
          className={`w-10 h-10 rounded-lg font-bold text-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${colors.button}`}
        >
          +
        </button>
      </div>

      {/* Presets rápidos */}
      {presets.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {presets.map(preset => (
            <button
              key={preset.value}
              onClick={() => onChange(preset.value.toString())}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                parseFloat(value) === preset.value ? colors.preset : colors.presetInactive
              }`}
              title={preset.label}
            >
              {preset.value}{unit}
              {preset.tag && <span className="ml-1 opacity-75">({preset.tag})</span>}
            </button>
          ))}
        </div>
      )}

      {/* Texto de ajuda */}
      {helpText && (
        <p className="text-xs text-gray-500">{helpText}</p>
      )}

      {/* Barra de progresso */}
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all ${
            colorClass === 'green' ? 'bg-green-500' :
            colorClass === 'purple' ? 'bg-purple-500' :
            'bg-orange-500'
          }`}
          style={{ width: `${((numValue - min) / (max - min)) * 100}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-400">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
};

const ProfitCalculator = () => {
  const { t } = useTranslation();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Dados de referência do mercado
  const [marketData, setMarketData] = useState(null);
  const [priceRanges, setPriceRanges] = useState(null);
  const [referenceComparison, setReferenceComparison] = useState(null);
  const [geb10Status, setGeb10Status] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Inputs do usuário
  // ALTERADO: GEB-10 APABOR como referência padrão (mais usada pelo mercado)
  const [referenceType, setReferenceType] = useState('geb10_apabor');
  const [customReferencePrice, setCustomReferencePrice] = useState('');
  const [drc, setDrc] = useState('53');
  // ALTERADO: Taxa padrão 72% (taxa implícita média do mercado)
  const [participationRate, setParticipationRate] = useState('72');
  const [productionKg, setProductionKg] = useState('');
  const [productionCost, setProductionCost] = useState('');
  
  // Resultado do cálculo
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const taxaMercadoLive = geb10Status?.implied_participation?.value ? geb10Status.implied_participation.value / 100 : 0.72;
  const coaguloLive = marketData?.coagulo?.value || null;

  // Presets para DRC
  const drcPresets = [
    { value: 45, tag: 'Baixo' },
    { value: 50, tag: 'Mín' },
    { value: 53, tag: 'Padrão' },
    { value: 55, tag: 'Bom' },
    { value: 58, tag: '' },
    { value: 60, tag: 'Ótimo' },
    { value: 65, tag: 'Excel.' },
  ];

  // Presets para Taxa de Participação
  const impliedRate = geb10Status?.implied_participation?.value || null;
  const geb10Est = geb10Status?.estimated?.value || null;
  const officialStale = !!geb10Status?.official_stale;

  const participationPresets = [
    ...(impliedRate ? [{ value: Math.round(impliedRate * 10) / 10, tag: 'Atual' }] : []),
    { value: 45, tag: 'Baixa' },
    { value: 50, tag: '' },
    { value: 52, tag: 'Mín CNA' },
    { value: 59, tag: 'Crise' },
    { value: 65, tag: '' },
    { value: 72, tag: 'Média' },
    { value: 75, tag: '' },
    { value: 80, tag: 'IEA' },
    { value: 85, tag: 'Coop.' },
  ];

  useEffect(() => {
    fetchMarketData();
  }, []);

  const fetchMarketData = async () => {
    try {
      const [quotationsRes, rangesRes, comparisonRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/market/quotations`),
        fetch(`${API_URL}/api/market/price-ranges`),
        fetch(`${API_URL}/api/market/reference-comparison`),
        fetch(`${API_URL}/api/market/geb10-status`)
      ]);

      if (quotationsRes.ok) {
        const data = await quotationsRes.json();
        const byType = {};
        data.quotations.forEach(q => byType[q.type] = q);
        setMarketData(byType);
      }

      if (rangesRes.ok) {
        const ranges = await rangesRes.json();
        setPriceRanges(ranges);
      }

      if (comparisonRes.ok) {
        const comparison = await comparisonRes.json();
        setReferenceComparison(comparison);
      }

      if (statusRes.ok) {
        const status = await statusRes.json();
        setGeb10Status(status);
        if (status.recommended_reference === 'geb10_estimado') {
          setReferenceType('geb10_estimado');
        }
        if (status.implied_participation?.value) {
          setParticipationRate(status.implied_participation.value.toString());
        }
      }

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    if (!drc || !participationRate) {
      alert('Informe o DRC e a Taxa de Participação');
      return;
    }

    setCalculating(true);

    try {
      const response = await fetch(`${API_URL}/api/market/calculate-price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reference_type: referenceType,
          reference_price: customReferencePrice ? parseFloat(customReferencePrice) : null,
          drc: parseFloat(drc),
          participation_rate: parseFloat(participationRate),
          production_kg: productionKg ? parseFloat(productionKg) : null,
          production_cost: productionCost ? parseFloat(productionCost) : null
        })
      });

      if (!response.ok) {
        throw new Error('Erro no cálculo');
      }

      const data = await response.json();
      setResult(data);

    } catch (err) {
      console.error('Erro:', err);
      alert('Erro ao calcular. Tente novamente.');
    } finally {
      setCalculating(false);
    }
  };

  const resetForm = () => {
    setReferenceType(geb10Status?.recommended_reference === 'geb10_estimado' ? 'geb10_estimado' : 'geb10_apabor');
    setCustomReferencePrice('');
    setDrc('53');
    setParticipationRate(impliedRate ? impliedRate.toString() : '72');
    setProductionKg('');
    setProductionCost('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Valores de referência
  const precoImportacao = marketData?.preco_importacao?.value || 13.91;
  const precoImportacaoAnt = marketData?.preco_importacao_anterior?.value || 12.81;
  const geb10 = marketData?.geb10_apabor?.value || 12.01;
  const costReference = marketData?.custo_producao_cot?.value || 6.07;

  const getReferenceValue = () => {
    if (customReferencePrice) return parseFloat(customReferencePrice);
    switch (referenceType) {
      case 'preco_importacao': return precoImportacao;
      case 'preco_importacao_anterior': return precoImportacaoAnt;
      case 'geb10_apabor': return geb10;
      case 'geb10_estimado': return geb10Est || geb10;
      default: return precoImportacaoAnt;
    }
  };

  // Cálculo em tempo real (preview)
  const previewPrice = () => {
    const ref = getReferenceValue();
    const d = parseFloat(drc) || 53;
    const p = parseFloat(participationRate) || 80;
    return (ref * (d/100) * (p/100)).toFixed(2);
  };

  return (
    <div className="space-y-6">
      {/* Alerta sobre diferença de referências */}
      <Card className="border-amber-300 bg-amber-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-2">
            <span className="text-xl">⚠️</span>
            <div>
              <p className="text-amber-800 font-medium">{t('pcImportant')}</p>
              <p className="text-amber-700 text-sm mt-1">
                {officialStale && geb10Est ? (
                  <>
                    O <strong>GEB-10 oficial APABOR</strong> (R$ {geb10.toFixed(2)}/kg • {geb10Status?.official?.reference_date}) está <strong>desatualizado</strong>.
                    Recomendamos o <strong>GEB-10 Estimado HeveaTalent</strong> (R$ {geb10Est.toFixed(2)}/kg) — calculado diariamente: TSR-20 (bolsa de Singapura) × dólar PTAX (BCB), calibrado pelo último GEB-10 oficial conhecido.
                  </>
                ) : (
                  <>
                    O <strong>GEB-10 APABOR</strong> (R$ {geb10.toFixed(2)}/kg) é a referência usada pelas usinas na prática.
                    O <strong>Preço de Importação (IEA/CNA)</strong> (R$ {precoImportacao.toFixed(2)}/kg) é o teto teórico.
                    <br/>
                    <strong>Diferença de {((precoImportacao/geb10 - 1) * 100).toFixed(0)}%.</strong> Ambas são válidas - entenda qual usar para cada situação.
                  </>
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fórmula */}
      <Card className="border-green-300 bg-green-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-2">
            <span className="text-xl">📐</span>
            <div className="flex-1">
              <p className="text-green-800 font-medium">{t('pcOfficialFormula')}</p>
              <div className="bg-white p-3 rounded-lg mt-2 font-mono text-sm">
                <p className="text-gray-800">
                  <span className="text-green-700">{t('pcProducerPrice')}</span> = 
                  <span className="text-blue-700"> Referência</span> × 
                  <span className="text-purple-700">(DRC/100)</span> × 
                  <span className="text-orange-700">(Taxa/100)</span>
                </p>
              </div>
              {/* Preview em tempo real */}
              <div className="mt-2 p-2 bg-green-100 rounded-lg">
                <p className="text-green-800 text-sm">
                  <strong>Preview:</strong> R$ {getReferenceValue().toFixed(2)} × {drc}% × {participationRate}% = 
                  <span className="text-xl font-bold ml-2">R$ {previewPrice()}/kg</span>
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulário */}
        <Card>
          <CardHeader>
            <CardTitle className="text-green-800">📝 Dados para Cálculo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Tipo de Referência */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preço de Referência
                </label>
                <select
                  value={referenceType}
                  onChange={(e) => setReferenceType(e.target.value)}
                  data-testid="reference-type-select"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  {geb10Est && (
                    <option value="geb10_estimado">🛰️ GEB-10 Estimado HeveaTalent (R$ {geb10Est.toFixed(2)}) - Atualizado diariamente</option>
                  )}
                  <option value="geb10_apabor">🏭 GEB-10 APABOR (R$ {geb10.toFixed(2)}) - Referência das usinas</option>
                  <option value="preco_importacao">📊 Importação (R$ {precoImportacao.toFixed(2)}) - Teto teórico IEA/CNA</option>
                  <option value="preco_importacao_anterior">📈 Importação Anterior (R$ {precoImportacaoAnt.toFixed(2)}) - Base negociações</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {referenceType === 'geb10_estimado' && '🛰️ Estimativa diária: TSR-20 (Singapura) × PTAX, calibrada pelo último GEB-10 oficial'}
                  {referenceType === 'geb10_apabor' && (officialStale ? `⚠️ Último valor oficial conhecido (${geb10Status?.official?.reference_date || ''}) - pode estar desatualizado` : '✅ Referência prática usada pelas usinas para negociar')}
                  {referenceType === 'preco_importacao' && '📊 Teto teórico - custo total de importação'}
                  {referenceType === 'preco_importacao_anterior' && '📊 Base mensal anterior para negociações'}
                </p>
              </div>

              {/* Valor personalizado */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ou informe valor personalizado (R$/kg)
                </label>
                <input
                  type="number"
                  value={customReferencePrice}
                  onChange={(e) => setCustomReferencePrice(e.target.value)}
                  placeholder="Deixe vazio para usar selecionado acima"
                  step="0.01"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              <hr />

              {/* DRC - Controle inteligente */}
              <NumberStepper
                value={drc}
                onChange={setDrc}
                min={30}
                max={75}
                step={1}
                unit="%"
                label="🌿 DRC - Teor de Borracha Seca"
                helpText="Mínimo aceito: 53% | Bom: 55-60% | Excelente: 60%+"
                presets={drcPresets}
                colorClass="purple"
              />

              {/* Taxa de Participação - Controle inteligente */}
              <NumberStepper
                value={participationRate}
                onChange={setParticipationRate}
                min={40}
                max={90}
                step={1}
                unit="%"
                label="💰 Taxa de Participação"
                helpText="Mínimo CNA: 52% | Referência IEA: 80% | Máximo histórico: 88,5%"
                presets={participationPresets}
                colorClass="orange"
              />

              {impliedRate && (
                <div className="flex items-center justify-between gap-2 p-2 bg-orange-50 border border-orange-200 rounded-lg" data-testid="implied-participation-banner">
                  <p className="text-xs text-orange-800">
                    📡 <strong>Participação atual do mercado: {impliedRate.toFixed(1)}%</strong>
                    <span className="block text-orange-600 mt-0.5">{geb10Status?.implied_participation?.source}</span>
                  </p>
                  <button
                    onClick={() => setParticipationRate(impliedRate.toString())}
                    data-testid="use-implied-participation-btn"
                    className="px-3 py-1.5 text-xs font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700 shrink-0"
                  >
                    Usar {impliedRate.toFixed(1)}%
                  </button>
                </div>
              )}

              <hr />

              {/* Produção (opcional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📦 Produção Mensal (kg) - <span className="text-gray-400">opcional</span>
                </label>
                <input
                  type="number"
                  value={productionKg}
                  onChange={(e) => setProductionKg(e.target.value)}
                  placeholder="Ex: 1000"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Custo de Produção (opcional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  💵 Custo de Produção (R$/kg) - <span className="text-gray-400">opcional</span>
                </label>
                <input
                  type="number"
                  value={productionCost}
                  onChange={(e) => setProductionCost(e.target.value)}
                  placeholder={`COT IEA: ${costReference.toFixed(2)}`}
                  step="0.01"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  COE: R$ {marketData?.custo_producao_coe?.value?.toFixed(2) || '4,99'} | COT: R$ {costReference.toFixed(2)}
                </p>
              </div>

              {/* Botões */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={calculatePrice}
                  disabled={calculating}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
                >
                  {calculating ? '⏳ Calculando...' : '📊 Calcular Detalhado'}
                </button>
                <button
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Limpar
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Resultado */}
        <Card className={result ? (result.comparison_with_market?.cost_coverage ? 'border-green-400' : 'border-red-400') : ''}>
          <CardHeader>
            <CardTitle className="text-green-800">📈 Resultado do Cálculo</CardTitle>
          </CardHeader>
          <CardContent>
            {!result ? (
              <div className="text-center py-8">
                <div className={`p-6 rounded-xl ${parseFloat(previewPrice()) >= costReference ? 'bg-green-100' : 'bg-red-100'}`}>
                  <p className="text-sm text-gray-600 mb-2">{t('pcRealTimePreview')}</p>
                  <p className={`text-5xl font-bold ${parseFloat(previewPrice()) >= costReference ? 'text-green-700' : 'text-red-700'}`}>
                    R$ {previewPrice()}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">por kg de coágulo</p>
                  <p className={`text-xs mt-2 ${parseFloat(previewPrice()) >= costReference ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(previewPrice()) >= costReference 
                      ? '✅ Acima do custo de produção' 
                      : '❌ Abaixo do custo de produção (R$ ' + costReference.toFixed(2) + ')'}
                  </p>
                </div>
                <p className="text-gray-400 text-sm mt-4">
                  Clique em "Calcular Detalhado" para ver análise completa
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Preço Calculado */}
                <div className={`p-4 rounded-lg ${result.comparison_with_market?.cost_coverage ? 'bg-green-100' : 'bg-red-100'}`}>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t('pcEstimatedPrice')}</p>
                    <p className={`text-4xl font-bold ${result.comparison_with_market?.cost_coverage ? 'text-green-700' : 'text-red-700'}`}>
                      R$ {result.producer_price_per_kg?.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {result.comparison_with_market?.cost_coverage 
                        ? '✅ Cobre custo de produção' 
                        : '❌ Abaixo do custo de produção'}
                    </p>
                  </div>
                </div>

                {/* Detalhamento */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">📐 Detalhamento:</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-blue-700">{t('pcReferenceLabel')}</span>
                      <span className="font-medium">R$ {result.reference_price?.toFixed(2)}/kg</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-purple-700">DRC:</span>
                      <span className="font-medium">{result.drc}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-orange-700">{t('pcParticipationRate')}</span>
                      <span className="font-medium">{result.participation_rate}%</span>
                    </div>
                    <hr className="my-2" />
                    <div className="flex justify-between font-bold">
                      <span className="text-green-700">{t('pcFinalPrice')}</span>
                      <span>R$ {result.producer_price_per_kg?.toFixed(2)}/kg</span>
                    </div>
                  </div>
                </div>

                {/* Comparativos */}
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-700">📊 Comparativos:</h4>
                  
                  <div className="flex justify-between p-2 bg-gray-50 rounded text-sm">
                    <span>{t('pcMarket')}</span>
                    <span className="font-medium">R$ {result.comparison_with_market?.market_coagulo_price?.toFixed(2)}/kg</span>
                  </div>
                  
                  <div className="flex justify-between p-2 bg-gray-50 rounded text-sm">
                    <span>{t('pcProductionCost')}</span>
                    <span className="font-medium text-orange-600">R$ {result.production_cost?.toFixed(2)}/kg</span>
                  </div>
                  
                  <div className={`flex justify-between p-2 rounded text-sm ${result.comparison_with_market?.cost_coverage ? 'bg-green-100' : 'bg-red-100'}`}>
                    <span>{t('pcMargin')}</span>
                    <span className={`font-bold ${result.comparison_with_market?.cost_coverage ? 'text-green-700' : 'text-red-700'}`}>
                      {result.comparison_with_market?.cost_difference >= 0 ? '+' : ''}
                      R$ {result.comparison_with_market?.cost_difference?.toFixed(2)}/kg
                    </span>
                  </div>
                </div>

                {/* Projeção de Receita */}
                {result.production_kg && (
                  <div className="pt-4 border-t">
                    <h4 className="font-medium text-gray-700 mb-2">📅 Projeção Mensal:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 bg-gray-50 rounded text-center">
                        <p className="text-xs text-gray-500">{t('pcProduction')}</p>
                        <p className="font-bold">{result.production_kg.toLocaleString()} kg</p>
                      </div>
                      <div className="p-2 bg-green-50 rounded text-center">
                        <p className="text-xs text-gray-500">{t('pcRevenue')}</p>
                        <p className="font-bold text-green-600">{formatCurrency(result.gross_revenue)}</p>
                      </div>
                      <div className="p-2 bg-orange-50 rounded text-center">
                        <p className="text-xs text-gray-500">{t('pcCost')}</p>
                        <p className="font-bold text-orange-600">{formatCurrency(result.total_cost)}</p>
                      </div>
                      <div className={`p-2 rounded text-center ${result.net_profit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                        <p className="text-xs text-gray-500">{t('pcProfitLoss')}</p>
                        <p className={`font-bold ${result.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {formatCurrency(result.net_profit)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabela comparativa de referências */}
      {referenceComparison && (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-800">🔍 Comparativo de Referências e Taxas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="bg-blue-100">
                    <th className="p-2 text-left">{t('maReference')}</th>
                    <th className="p-2 text-right">{t('pcValue')}</th>
                    <th className="p-2 text-right">Taxa 52%</th>
                    <th className="p-2 text-right">Taxa 60%</th>
                    <th className="p-2 text-right">Taxa 70%</th>
                    <th className="p-2 text-right bg-green-100">Taxa {Math.round(taxaMercadoLive * 100)}%</th>
                    <th className="p-2 text-right">Taxa 80%</th>
                  </tr>
                </thead>
                <tbody>
                  {referenceComparison.comparison_table?.map((row, idx) => {
                    const ref = row.value;
                    const d = 0.53;
                    const taxa72Value = ref * d * taxaMercadoLive;
                    const isCloseToMarket = coaguloLive ? Math.abs(taxa72Value - coaguloLive) < 0.15 : false;
                    return (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="p-2 font-medium text-xs">{row.reference}</td>
                        <td className="p-2 text-right">R$ {ref?.toFixed(2)}</td>
                        <td className="p-2 text-right text-gray-600">R$ {(ref * d * 0.52).toFixed(2)}</td>
                        <td className="p-2 text-right text-gray-600">R$ {(ref * d * 0.60).toFixed(2)}</td>
                        <td className="p-2 text-right text-gray-600">R$ {(ref * d * 0.70).toFixed(2)}</td>
                        <td className={`p-2 text-right font-bold ${isCloseToMarket ? 'bg-green-200 text-green-800' : 'bg-green-50 text-green-700'}`}>
                          R$ {taxa72Value.toFixed(2)}
                          {isCloseToMarket && ' ✓'}
                        </td>
                        <td className="p-2 text-right text-gray-600">R$ {(ref * d * 0.80).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                  {/* Linha do preço atual de mercado */}
                  <tr className="bg-green-100 border-t-2 border-green-400">
                    <td className="p-2 font-bold text-green-800" colSpan="2">📍 Coágulo DRC 53% (Mercado)</td>
                    <td className="p-2 text-right text-gray-400" colSpan="4"></td>
                    <td className="p-2 text-right font-bold text-green-800">{coaguloLive ? `R$ ${coaguloLive.toFixed(2).replace('.', ',')}` : '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
              <span className="px-2 py-1 bg-green-200 rounded">Taxa {Math.round(taxaMercadoLive * 100)}%</span>
              <span>= Taxa implícita atual do mercado ({(taxaMercadoLive * 100).toFixed(1).replace('.', ',')}% sobre a referência, atualizada diariamente)</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Informações */}
      <Card>
        <CardHeader>
          <CardTitle className="text-green-800">💡 Informações para Negociação</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-bold text-purple-800 mb-2">🌿 Sobre o DRC</h4>
              <p className="text-sm text-gray-600">
                Cada 1% de DRC impacta ~R$ 0,10-0,15/kg no valor.
                O padrão de referência é 53%.
              </p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg">
              <h4 className="font-bold text-orange-800 mb-2">📊 Sobre a Taxa</h4>
              <p className="text-sm text-gray-600">
                Referências: CNA 52% (mínimo) | IEA 80%.
                Taxa de mercado atual: {(taxaMercadoLive * 100).toFixed(1).replace('.', ',')}% (implícita, atualizada diariamente).
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-bold text-blue-800 mb-2">🔍 Transparência</h4>
              <p className="text-sm text-gray-600">
                A fórmula padrão: Referência × DRC × Taxa.
                Use esta calculadora para simular cenários.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfitCalculator;
